<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="loadError">
    <p role="alert">{{ loadError }}</p>
    <p-button label="Back to Drivers" icon="pi pi-arrow-left" routerLink="/fleet/drivers"></p-button>
</app-page-shell>

<app-page-shell *ngIf="!loadError">

    <app-page-hero
        eyebrow="Fleet Drivers"
        [title]="formTitle"
        [subtitle]="driver.id ? 'Update driver profile, documents, licensing, and assigned vehicle.' : 'Create a driver profile with licensing, documents, address, and vehicle assignment.'">
        <div actions>
            <p-button *ngIf="embedded" label="Cancel" icon="fa-solid fa-xmark" severity="secondary" [outlined]="true" (onClick)="cancelEmbedded()"></p-button>
            <p-button label="Clear" icon="fa-solid fa-xmark" severity="secondary" [outlined]="true" (onClick)="clearForm()"></p-button>
            <p-button [label]="driver.id ? 'Save Changes' : 'Create Driver'" icon="fa-solid fa-floppy-disk"
                [loading]="driverCreationInProgress" (onClick)="saveDriver()" [disabled]="driverCreationInProgress"></p-button>
        </div>
    </app-page-hero>

    <form class="overFlow" [formGroup]="createEditDriverForm" novalidate autocomplete="off" autocorrect="off"
        autocapitalize="off" spellcheck="off">
        <div class="p-fluid fleet-form-stack">
            <app-page-form-section
                icon="fa-solid fa-user-tie"
                title="Personal Information"
                subtitle="Driver identity and nationality details.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('nameInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-user"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="driverName" type="text" pInputText [(ngModel)]="driver.name"
                                    formControlName="nameInput" />
                                <label for="driverName">Driver Name</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('nameInput')) {
                        <small class="p-error field-error">{{ getFieldError('nameInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('nationalityDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-flag"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="nationality" [options]="countries" [(ngModel)]="selectedCountry"
                                    optionLabel="name" (onChange)="onCountryChange($event)" [filter]="true"
                                    filterBy="name" formControlName="nationalityDropdown" styleClass="w-full">
                                </p-select>
                                <label for="nationality">Nationality</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('nationalityDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('nationalityDropdown') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('dateOfBirthInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker id="dateOfBirth" [(ngModel)]="selectedDateOfBirth"
                                    dateFormat="dd/mm/yy" formControlName="dateOfBirthInput" styleClass="w-full"></p-datePicker>
                                <label for="dateOfBirth">Date of Birth</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('dateOfBirthInput')) {
                        <small class="p-error field-error">{{ getFieldError('dateOfBirthInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('genderDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-venus-mars"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="gender" [options]="genderOptions" optionLabel="label" optionValue="value"
                                    formControlName="genderDropdown" styleClass="w-full">
                                </p-select>
                                <label for="gender">Gender</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('genderDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('genderDropdown') }}</small>
                        }
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section
                icon="fa-solid fa-address-book"
                title="Contact & Address"
                subtitle="Phone, email, residing country, city, and home location.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('phoneNumberInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-phone"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="phoneNumber" type="text" pInputText [(ngModel)]="driver.phoneNumber"
                                    formControlName="phoneNumberInput" [placeholder]="phonePlaceholder"
                                    [title]="phoneMask" (blur)="normalizePhoneInput()" />
                                <label for="phoneNumber">Phone Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('phoneNumberInput')) {
                        <small class="p-error field-error">{{ getFieldError('phoneNumberInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-envelope"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="emailAddress" type="text" pInputText [(ngModel)]="driver.emailAddress"
                                    formControlName="emailAddressInput" />
                                <label for="emailAddress">Email Address</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('residingCountryDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-globe"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="residingCountry" [options]="countries"
                                    [(ngModel)]="selectedResidingCountry" optionLabel="name"
                                    (onChange)="onResidingCountryChange($event)" [filter]="true" filterBy="name"
                                    formControlName="residingCountryDropdown" styleClass="w-full">
                                </p-select>
                                <label for="residingCountry">Residing Country</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('residingCountryDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('residingCountryDropdown') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('residingCityDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-city"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="residingCity" [options]="residingCities"
                                    [(ngModel)]="selectedResidingCity" optionLabel="cityName"
                                    (onChange)="onResidingCityChange($event)" [filter]="true" filterBy="cityName"
                                    formControlName="residingCityDropdown" [disabled]="!selectedResidingCountry"
                                    styleClass="w-full">
                                </p-select>
                                <label for="residingCity">Residing City</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('residingCityDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('residingCityDropdown') }}</small>
                        }
                    </div>
                    <div class="field col-12">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-house"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-autoComplete id="homeLocation" [suggestions]="homeLocationOptions"
                                    [(ngModel)]="selectedHomeLocation" optionLabel="name"
                                    (completeMethod)="searchHomeLocations($event)"
                                    (onSelect)="onHomeLocationChange($event)"
                                    (onClear)="onHomeLocationChange({ value: null })"
                                    formControlName="homeLocationDropdown" [dropdown]="true"
                                    [forceSelection]="true" appendTo="body" styleClass="w-full">
                                </p-autoComplete>
                                <label for="homeLocation">Home Location</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section
                icon="fa-solid fa-id-card"
                title="Residency & Licensing"
                subtitle="Iqama and license numbers with expiry dates.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('identityTypeInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-passport"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="identityType" [options]="identityTypeOptions" optionLabel="label"
                                    optionValue="value" formControlName="identityTypeInput" styleClass="w-full"></p-select>
                                <label for="identityType">Identity Type</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('identityTypeInput')) {
                        <small class="p-error field-error">{{ getFieldError('identityTypeInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('identityIssueNumberInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="identityIssueNumber" type="number" pInputText
                                    formControlName="identityIssueNumberInput" />
                                <label for="identityIssueNumber">Identity Issue Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('identityIssueNumberInput')) {
                        <small class="p-error field-error">{{ getFieldError('identityIssueNumberInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('residenceNumberInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-id-card"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="residenceNumber" type="text" pInputText [(ngModel)]="driver.residenceNumber"
                                    formControlName="residenceNumberInput" />
                                <label for="residenceNumber">Residence Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('residenceNumberInput')) {
                        <small class="p-error field-error">{{ getFieldError('residenceNumberInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('residenceExpiryDateInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar-times"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker id="residenceExpiryDate" [(ngModel)]="selectedResidenceExpiryDate"
                                    dateFormat="dd/mm/yy" formControlName="residenceExpiryDateInput" styleClass="w-full"></p-datePicker>
                                <label for="residenceExpiryDate">Residence Expiry</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('residenceExpiryDateInput')) {
                        <small class="p-error field-error">{{ getFieldError('residenceExpiryDateInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('licenseNumberInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-address-card"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="licenseNumber" type="text" pInputText [(ngModel)]="driver.licenseNumber"
                                    formControlName="licenseNumberInput" />
                                <label for="licenseNumber">License Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('licenseNumberInput')) {
                        <small class="p-error field-error">{{ getFieldError('licenseNumberInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('licenseExpiryDateInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar-times"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker id="licenseExpiryDate" [(ngModel)]="selectedLicenseExpiryDate"
                                    dateFormat="dd/mm/yy" formControlName="licenseExpiryDateInput" styleClass="w-full"></p-datePicker>
                                <label for="licenseExpiryDate">License Expiry</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('licenseExpiryDateInput')) {
                        <small class="p-error field-error">{{ getFieldError('licenseExpiryDateInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('licenseCountryDropdown')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-flag-checkered"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="licenseCountry" [options]="countries" [(ngModel)]="selectedLicenseCountry"
                                    optionLabel="name" (onChange)="onLicenseCountryChange($event)" [filter]="true"
                                    filterBy="name" formControlName="licenseCountryDropdown" styleClass="w-full">
                                </p-select>
                                <label for="licenseCountry">License Country</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('licenseCountryDropdown')) {
                        <small class="p-error field-error">{{ getFieldError('licenseCountryDropdown') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('licenseClassInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-layer-group"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="licenseClass" type="text" pInputText formControlName="licenseClassInput" />
                                <label for="licenseClass">License Class</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('licenseClassInput')) {
                        <small class="p-error field-error">{{ getFieldError('licenseClassInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6" [class.validation-field-invalid]="isFieldInvalid('licenseTypeInput')">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-list-check"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="licenseType" type="text" pInputText formControlName="licenseTypeInput" />
                                <label for="licenseType">License Type</label>
                            </p-iftalabel>
                        </p-inputGroup>
                        @if (isFieldInvalid('licenseTypeInput')) {
                        <small class="p-error field-error">{{ getFieldError('licenseTypeInput') }}</small>
                        }
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-id-badge"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="transportPermitNumber" type="text" pInputText
                                    formControlName="transportPermitNumberInput" />
                                <label for="transportPermitNumber">Transport Permit Number</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-calendar-check"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-datePicker id="transportPermitExpiryDate" [(ngModel)]="selectedTransportPermitExpiryDate"
                                    dateFormat="dd/mm/yy" formControlName="transportPermitExpiryDateInput"
                                    styleClass="w-full"></p-datePicker>
                                <label for="transportPermitExpiryDate">Transport Permit Expiry</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-earth-asia"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="transportPermitCountry" [options]="countries"
                                    [(ngModel)]="selectedTransportPermitCountry" optionLabel="name"
                                    (onChange)="onTransportPermitCountryChange($event)" [filter]="true"
                                    filterBy="name" formControlName="transportPermitCountryDropdown"
                                    [showClear]="true" styleClass="w-full">
                                </p-select>
                                <label for="transportPermitCountry">Transport Permit Country</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <app-page-form-section
                icon="fa-solid fa-folder-open"
                title="Documents & Vehicle Assignment"
                subtitle="Upload driver documents and assign the current vehicle.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-5">
                        <p-fileUpload name="residenceCardFile" accept=".pdf,.png,.jpg" mode="basic"
                            [customUpload]="true" (onSelect)="onResidenceCardUpload($event)"
                            chooseLabel="Upload Residence Card" chooseIcon="fa-solid fa-id-card"
                            styleClass="w-full"></p-fileUpload>
                    </div>
                    <div class="field col-12 md:col-1 flex align-items-center justify-content-center">
                        @if (driver.residenceImage) {
                        <img [src]="driver.residenceImage" alt="Residence" class="document-preview" />
                        }
                    </div>
                    <div class="field col-12 md:col-5">
                        <p-fileUpload name="licenseCardFile" accept=".pdf,.png,.jpg" mode="basic" [customUpload]="true"
                            (onSelect)="onLicenseCardUpload($event)" chooseLabel="Upload License Card"
                            chooseIcon="fa-solid fa-address-card" styleClass="w-full"></p-fileUpload>
                    </div>
                    <div class="field col-12 md:col-1 flex align-items-center justify-content-center">
                        @if (driver.licenseImage) {
                        <img [src]="driver.licenseImage" alt="License" class="document-preview" />
                        }
                    </div>
                    <div class="field col-12">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-truck"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="currentVehicle" [options]="availableVehicles"
                                    [(ngModel)]="selectedVehicle" optionLabel="plateNumber"
                                    (onChange)="onVehicleChange($event)" formControlName="currentVehicleDropdown"
                                    placeholder="Optional" [showClear]="true" styleClass="w-full">
                                </p-select>
                                <label for="currentVehicle">Current Vehicle</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            @if (isRental || isFreelancer) {
            <app-page-form-section
                icon="fa-solid fa-building-user"
                title="External Ownership"
                subtitle="Link this driver to the exact transporter CRM client when it belongs to a third-party transporter.">
                <div class="formgrid grid">
                    <div class="field col-12">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="fa-solid fa-truck-ramp-box"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select id="transporterClient" [options]="transporterClients"
                                    [(ngModel)]="selectedTransporterClient" optionLabel="companyName"
                                    (onChange)="onTransporterClientChange($event)" [filter]="true"
                                    filterBy="companyName,clientNumber,linkedTransportationAgencyName"
                                    [showClear]="true" [ngModelOptions]="{standalone: true}"
                                    placeholder="Optional" styleClass="w-full">
                                </p-select>
                                <label for="transporterClient">Transporter CRM Client</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>
                </div>
                @if (isRental) {
                <div class="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3 mt-3 p-3 surface-50 border-1 border-200 border-round">
                    <span class="text-700">Rental agreement, rate cards, and settlement terms are managed centrally in Fleet &gt; Rental &gt; Agreements.</span>
                    <p-button label="Open Rental Agreements" icon="pi pi-external-link" severity="secondary" [outlined]="true"
                        [routerLink]="['/fleet/rentals/agreements']"></p-button>
                </div>
                }
            </app-page-form-section>
            }
        </div>
    </form>
</app-page-shell>
